type DetermineCurrentPageLocation = {
    currentPage: number;
    numSlots: number;
    totalPages: number;
};

function beginPages({
    currentPage,
    numSlots,
}: DetermineCurrentPageLocation): boolean {
    return currentPage < numSlots - 1;
}

function endPages({
    currentPage,
    numSlots,
    totalPages,
}: DetermineCurrentPageLocation): boolean {
    return currentPage > totalPages - (numSlots - Math.floor(numSlots / 2));
}

function pageInMiddle(values: DetermineCurrentPageLocation): boolean {
    return !beginPages(values) && !endPages(values);
}

type Pagination = {
    startPage: number;
    currentPage: number;
    numSlots: number;
    totalItems: number;
    perPage: number;
};

export function usePagination({
    startPage,
    currentPage,
    numSlots,
    perPage,
    totalItems,
}: Pagination) {
    let links: Array<string | number> = [];
    const totalPages = Math.ceil(totalItems / perPage);
    const values = {
        currentPage,
        numSlots,
        totalPages,
    };
    const currentPageInMiddle = pageInMiddle(values);
    const currentPageInBeginning = beginPages(values);
    const currentPageInEnd = endPages(values);
    const showAllPages = totalPages <= numSlots;

    if (showAllPages) {
        links = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else if (currentPageInMiddle) {
        const slotsToFill = numSlots - 4;
        const indexes = Array.from(
            {
                length:
                    Math.ceil(slotsToFill / 2) + Math.floor(slotsToFill / 2),
            },
            (_, i) => Math.floor(slotsToFill / 2) - i,
        );
        links.unshift('...');
        links.unshift(startPage);
        indexes.forEach(i => links.push(currentPage - i));
        links.push('...');
        links.push(totalPages);
    } else if (currentPageInEnd) {
        const slotsToFill = numSlots - 2;
        const fillers = Array.from(
            { length: slotsToFill },
            (_, i) => totalPages - numSlots + i + 3,
        );
        links.unshift('...');
        links.unshift(startPage);
        links = links.concat(fillers);
    } else if (currentPageInBeginning) {
        const slotsToFill = numSlots - 2;
        const fillers = Array.from({ length: slotsToFill }, (_, i) => i + 1);
        links = links.concat(fillers);
        links.push('...');
        links.push(totalPages);
    }

    return {
        links,
        totalPages,
        showAllPages: totalPages <= numSlots,
        currentPageInMiddle,
        currentPageInBeginning,
        currentPageInEnd,
    };
}
